import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Plus, Download, Calculator, ReceiptText, Loader2 } from "lucide-react";
import { api, pdfUrl, downloadPdf } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { CATEGORIES, CATEGORY_COLORS, fmtMoney } from "@/lib/theme";
import { StatCard } from "@/components/StatCard";
import { CategoryChart } from "@/components/CategoryChart";
import { Skeleton } from "@/components/ui/skeleton";

function QuickAction({ icon: Icon, label, onClick, testID }) {
  return (
    <button
      onClick={onClick}
      data-testid={testID}
      className="flex flex-1 items-center justify-center gap-2.5 rounded-xl border border-rig-border bg-rig-surface px-4 py-4 text-sm font-semibold text-white transition-all hover:border-rig-accent hover:bg-rig-surface2"
    >
      <Icon className="h-5 w-5 text-rig-accent" strokeWidth={2.2} />
      {label}
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api("/dashboard");
      setData(d);
    } catch (e) {
      console.error("Failed to load dashboard:", e);
    }
  }, []);

  useEffect(() => {
    document.title = "Dashboard · RigBooks";
    load();
  }, [load]);

  const chartData = CATEGORIES.map((c) => ({
    category: c,
    total: data?.month_by_category?.[c]?.total || 0,
  }));

  const year = new Date().getFullYear();
  const exportPdf = async () => {
    setExporting(true);
    try {
      await downloadPdf(pdfUrl(`date_from=${year}-01-01&date_to=${year}-12-31`), `rigbooks_${year}.pdf`);
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64 bg-rig-surface" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 bg-rig-surface" />
          ))}
        </div>
        <Skeleton className="h-64 bg-rig-surface" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-rig-dim">
          {(data.month_label || "").toUpperCase()} · Overview
        </p>
        <h2 className="mt-1 font-display text-4xl font-bold tracking-wide text-white">
          Hey, {user?.name || user?.email?.split("@")[0] || "Driver"}
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="This Month" value={fmtMoney(data.month_total)} testID="stat-monthly" />
        <StatCard label="This Week" value={fmtMoney(data.week_total)} sub={`${data.week_count} receipts`} testID="stat-weekly" />
        <StatCard label="Year to Date" value={fmtMoney(data.ytd_total)} accent="#00E676" testID="stat-ytd" />
        <StatCard
          label="Audit-Ready"
          value={data.total_receipts}
          sub="stored & timestamped"
          accent="#00E676"
          icon={<ShieldCheck className="h-5 w-5 text-rig-accent" />}
          testID="stat-total"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">
            Expense Overview · This Month
          </h3>
          <CategoryChart data={chartData} />
        </div>

        <div>
          <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">
            Quick Actions
          </h3>
          <div className="flex flex-col gap-3">
            <QuickAction icon={Plus} label="New Receipt" onClick={() => navigate("/add")} testID="quick-add" />
            <QuickAction
              icon={exporting ? Loader2 : Download}
              label={exporting ? "Exporting…" : "Export Year PDF"}
              onClick={exportPdf}
              testID="quick-export"
            />
            <QuickAction icon={Calculator} label="Taxes & IFTA" onClick={() => navigate("/taxes")} testID="quick-taxes" />
          </div>
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">
            Recent Receipts
          </h3>
          <Link to="/receipts" className="text-sm font-semibold text-rig-accent hover:underline">
            View all
          </Link>
        </div>
        {data.recent_receipts.length === 0 ? (
          <div
            data-testid="empty-receipts"
            className="flex flex-col items-center rounded-2xl border border-dashed border-rig-borderStrong bg-rig-surface p-10 text-center"
          >
            <ReceiptText className="h-10 w-10 text-rig-mute" />
            <p className="mt-3 text-white">No receipts yet</p>
            <p className="mt-1 text-sm text-rig-dim">Add your first receipt to start your audit trail.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.recent_receipts.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/receipts/${r.id}`)}
                data-testid={`receipt-row-${r.id}`}
                className="flex w-full items-center gap-4 rounded-xl border border-rig-border bg-rig-surface px-4 py-3.5 text-left transition-colors hover:border-rig-borderStrong hover:bg-rig-surface2"
              >
                <span
                  className="h-9 w-1.5 rounded-full"
                  style={{ background: CATEGORY_COLORS[r.category] || "#94A3B8" }}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-white">{r.vendor}</div>
                  <div className="text-sm text-rig-dim">
                    {r.receipt_date} · {r.category}
                  </div>
                </div>
                <div className="font-display text-lg font-bold text-white">-{fmtMoney(r.amount)}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
